import React, { useCallback, useState } from "react";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import WebRounded from "@mui/icons-material/WebRounded";
import CalendarTodayRounded from "@mui/icons-material/CalendarTodayRounded";
import EventAvailableRounded from "@mui/icons-material/EventAvailableRounded";
import SyncAltRounded from "@mui/icons-material/SyncAltRounded";
import NotificationsActiveRounded from "@mui/icons-material/NotificationsActiveRounded";
import TranslateRounded from "@mui/icons-material/TranslateRounded";
import PersonAddRounded from "@mui/icons-material/PersonAddRounded";
import LocalOfferRounded from "@mui/icons-material/LocalOfferRounded";
import BarChartRounded from "@mui/icons-material/BarChartRounded";
import CakeRounded from "@mui/icons-material/CakeRounded";
import logo from "../img/Logo.svg";
import Header from "./Dashboard/Header";
import Footer from "./Dashboard/Footer";
import PageLayout from "./PageLayout";
import AlertModal from "./Modals/AlertModal";
import { CARD_STYLES, FONTS } from "../constants/styles";
import { NavigateFunction, useNavigate } from "react-router-dom";

const sections = [
    {
        label: "CORE TOOLS",
        capabilities: [
            {
                icon: <WebRounded />,
                title: "Create",
                description:
                    "Your website and two apps for you and your clients.",
            },
            {
                icon: <CalendarTodayRounded />,
                title: "Manage",
                description: "Your calendar and online bookings.",
            },
            {
                icon: <EventAvailableRounded />,
                title: "Fill",
                description: "Empty slots in your schedule.",
            },
            {
                icon: <SyncAltRounded />,
                title: "Coordinate",
                description:
                    "Rescheduling and cancellations with everyone involved.",
            },
        ],
    },
    {
        label: "SMART AUTOMATION",
        capabilities: [
            {
                icon: <NotificationsActiveRounded />,
                title: "Remind",
                description:
                    "Clients about appointments, payments, and leaving positive reviews.",
            },
            {
                icon: <TranslateRounded />,
                title: "Translate",
                description: "Instant translation into any user's language.",
            },
            {
                icon: <PersonAddRounded />,
                title: "Register",
                description: "Users and set up their personal accounts.",
            },
        ],
    },
    {
        label: "COMING SOON",
        capabilities: [
            {
                icon: <LocalOfferRounded />,
                title: "Present",
                description:
                    "Your promotions, updates, and loyalty programs.",
            },
            {
                icon: <BarChartRounded />,
                title: "Analyze",
                description:
                    "Your business (services, clients, time, money).",
            },
            {
                icon: <CakeRounded />,
                title: "Congratulate",
                description: "Your clients on special occasions.",
            },
        ],
    },
];

const getCapabilityDetails = (navigate: NavigateFunction) => [
    {
        title: "Create",
        description: (
            <>
                I've already built your app! Send the link to your clients —
                they can book appointments, browse services, get reminders, and
                recommend you to friends.
            </>
        ),
        actionText: "Share",
        onAction: () => navigate("/menu/share"),
    },
    {
        title: "Manage",
        description: (
            <>
                I manage your schedule — but you control when online booking is
                available.
                <br />
                <br />
                <strong>Enabled</strong> – clients choose a time that suits
                them.
                <br />
                <strong>Disabled</strong> – clients see a message: <br />
                <i>
                    "All slots are currently taken, but you can leave a request.
                    I'll try to find a time that works for you."
                </i>
                <br />
                <br />
                They'll be added to your waitlist, and you choose when and where
                to schedule them. It helps you stay in control and keep loyalty
                high. Your calendar is in good hands.
            </>
        ),
        actionText: "Automation settings",
        onAction: () => navigate("/menu/automations"),
    },
    {
        title: "Fill",
        description: (
            <>Choose a day — I'll help you fill any empty time slots.</>
        ),
        actionText: "Fill my day",
        onAction: () => navigate("/calendar"),
    },
    {
        title: "Coordinate",
        description: (
            <>
                I'll help you handle rescheduling and cancellations smoothly —
                and keep all participants updated.
            </>
        ),
        actionText: "Free my day",
        onAction: () => navigate("/calendar"),
    },
    {
        title: "Remind",
        description: (
            <>
                I remind clients about their visits, request confirmations, and
                ask them to leave reviews.
            </>
        ),
        actionText: "Set up reminders",
        onAction: () => navigate("/menu/automations"),
    },
    {
        title: "Translate",
        description: (
            <>
                I show clients the interface and messages in their own language.
                I also provide instant translations in chat. Everyone writes and
                reads in the language they're comfortable with.
            </>
        ),
        actionText: "Got it!",
    },
    {
        title: "Register",
        description: (
            <>
                Create client profiles and calculate revenue analytics for them.
            </>
        ),
        actionText: "Got it!",
    },
    {
        title: "Present",
        description: (
            <>
                In the future — with your permission — I'll automatically create
                and share promotions, bonuses, news, and loyalty program offers
                with your clients.
            </>
        ),
        actionText: "Got it",
    },
    {
        title: "Analyze",
        description: (
            <>
                Coming soon: analytics for your services, time, revenue, and
                clients. You'll clearly see what works and what to improve.
            </>
        ),
        actionText: "Got it",
    },
    {
        title: "Congratulate",
        description: (
            <>
                Want to wish clients a happy birthday or holiday? I'll remind
                you — and send it out for you.
            </>
        ),
        actionText: "Got it!",
    },
];

const CapabilityCard = ({
    icon,
    title,
    description,
    primary,
    index,
    onClick,
}: {
    icon: React.ReactNode;
    title: string;
    description: string;
    primary: string;
    index: number;
    onClick: () => void;
}) => (
    <Stack
        onClick={onClick}
        direction="column"
        alignItems="center"
        spacing={1.5}
        sx={{
            ...CARD_STYLES,
            cursor: "pointer",
            textAlign: "center",
            transition: "transform 0.15s ease, box-shadow 0.15s ease",
            "&:active": {
                transform: "scale(0.97)",
            },
            "@keyframes cardFadeIn": {
                from: { opacity: 0, transform: "translateY(16px)" },
                to: { opacity: 1, transform: "translateY(0)" },
            },
            animation: `cardFadeIn 0.4s ease-out ${0.15 + index * 0.05}s both`,
        }}
    >
        <Box
            sx={{
                width: 40,
                height: 40,
                borderRadius: "12px",
                bgcolor: alpha(primary, 0.1),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: primary,
                "& .MuiSvgIcon-root": { fontSize: 20 },
            }}
        >
            {icon}
        </Box>
        <Stack spacing={0.5} alignItems="center">
            <Typography
                fontFamily={FONTS.display}
                fontSize="15px"
                fontWeight={600}
                color="text.primary"
            >
                {title}
            </Typography>
            <Typography fontSize="12px" color="text.secondary" lineHeight={1.4}>
                {description}
            </Typography>
        </Stack>
    </Stack>
);

const Landing: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const primary = theme.palette.primary.main;
    const capabilityDetails = getCapabilityDetails(navigate);

    const [scrollY, setScrollY] = useState(0);
    const handleScrollY = useCallback((y: number) => setScrollY(y), []);

    const [selectedCapability, setSelectedCapability] = useState<null | {
        title: string;
        description: React.ReactNode;
        actionText?: string;
        onAction?: () => void;
    }>(null);

    let globalIndex = 0;

    const hero = (
        <Stack alignItems="center" spacing={1.5} sx={{ pt: 1, pb: 1 }}>
            <Box
                sx={{
                    width: 72,
                    height: 72,
                    borderRadius: "20px",
                    bgcolor: "rgba(255, 255, 255, 0.18)",
                    backdropFilter: "blur(12px)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                }}
            >
                <img
                    src={logo}
                    alt="JEMLOC"
                    style={{
                        width: 44,
                        height: 44,
                        filter: "brightness(0) invert(1)",
                    }}
                />
            </Box>
            <Typography
                fontFamily={FONTS.display}
                fontSize="26px"
                fontWeight={700}
                color="white"
                lineHeight={1.2}
            >
                Hello! I'm JEMLOC
            </Typography>
            <Typography
                fontSize="14px"
                color="rgba(255, 255, 255, 0.8)"
                fontFamily={FONTS.body}
            >
                Your AI business administrator
            </Typography>
        </Stack>
    );

    return (
        <>
            <Header scrollY={scrollY} />
            <PageLayout hero={hero} onScrollY={handleScrollY}>
                {sections.map((section) => (
                    <Box key={section.label}>
                        <Typography
                            fontSize="11px"
                            fontWeight={600}
                            color="text.secondary"
                            textTransform="uppercase"
                            letterSpacing="0.5px"
                            mb={1.5}
                        >
                            {section.label}
                        </Typography>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns:
                                    "repeat(auto-fit, minmax(200px, 1fr))",
                                gap: 1.5,
                            }}
                        >
                            {section.capabilities.map((cap) => {
                                const idx = globalIndex++;
                                return (
                                    <CapabilityCard
                                        key={cap.title}
                                        icon={cap.icon}
                                        title={cap.title}
                                        description={cap.description}
                                        primary={primary}
                                        index={idx}
                                        onClick={() => {
                                            const detail =
                                                capabilityDetails.find(
                                                    (d) =>
                                                        d.title === cap.title
                                                );
                                            if (detail)
                                                setSelectedCapability(detail);
                                        }}
                                    />
                                );
                            })}
                        </Box>
                    </Box>
                ))}
            </PageLayout>
            <Footer tab="admin" />
            <AlertModal
                open={!!selectedCapability}
                onClose={() => setSelectedCapability(null)}
                headingText={selectedCapability?.title}
                alertText={selectedCapability?.description}
                buttonText={selectedCapability?.actionText}
                onAction={selectedCapability?.onAction}
            />
        </>
    );
};

export default Landing;
